import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import Stack from "@mui/material/Stack";
import { type SvgIconProps, type SvgIconTypeMap } from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import type { ReactElement } from "react";
import type { ImageProps } from "../types";
import { CdnImage } from "./common/CdnImage";
import { MdaiLink } from "./MdaiLink";

export interface MdaiCardProps {
  Icon?:
    | (OverridableComponent<SvgIconTypeMap> & {
        muiName: string;
      })
    | React.FC<SvgIconProps>;
  iconStyle?: React.CSSProperties;
  image?: ImageProps;
  imageLayout?: "row" | "column";
  title: string;
  body: string | ReactElement;
  style?: React.CSSProperties;
  contentSpacing?: number;
  links?: { label: string; href: string; type?: "button" | "link" }[];
  tag?: string;
  minLgHeight?: number;
}

export const MdaiCard = ({
  card: {
    title,
    body,
    Icon,
    iconStyle,
    image,
    imageLayout,
    style,
    contentSpacing,
    links,
    tag,
    minLgHeight,
  },
}: {
  card: MdaiCardProps;
}) => {
  return (
    <Card
      key={title}
      sx={{
        minHeight: {
          sm: "fit-content",
          md: "100%",
          lg: minLgHeight ?? "100%",
        },
        flex: 1,
        width: "100%",
        height: "100%",
        backgroundColor: "var(--mdai-dark-purple-2)",
        padding: 3,
        ...style,
      }}
    >
      <CardContent sx={{ width: "100%", height: "100%", p: 0 }}>
        <Stack
          spacing={contentSpacing ?? 2}
          direction={image && imageLayout !== "column" ? "row" : "column"}
          height={"100%"}
          alignItems={"stretch"}
          alignContent={"space-between"}
        >
          {image && (
            <CdnImage
              sx={{
                width: imageLayout === "column" ? "100%" : (image.width ?? "132px"),
                minWidth: imageLayout === "column" ? "100%" : "132px",
                maxWidth:
                  imageLayout === "column" ? "100%" : (image.width ?? "132px"),
                order: 0,
                borderRadius: 2,
                objectFit: "cover",
              }}
              alt={image.alt ?? title}
              src={image.src}
            />
          )}
          <Stack spacing={contentSpacing ?? 2} direction={"column"}>
            <Stack
              direction={"row"}
              spacing={2}
              alignItems="center"
              justifyContent={tag ? "space-between" : "flex-start"}
              alignContent={"space-between"}
            >
              {Icon && (
                <Icon
                  sx={{
                    backgroundColor:
                      iconStyle?.backgroundColor ?? "var(--mdai-deep-purple)",
                    fill: iconStyle?.fill ?? "var(--mdai-dark-grey)",
                    borderRadius: 100,
                    padding: "4px",
                    fontSize: 36,
                    mb: 2,
                    ...iconStyle,
                  }}
                />
              )}
              <Typography variant="h5" component="div">
                {title}
              </Typography>
              {tag && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: "#220C28",
                    borderRadius: "4px",
                    padding: "2px 6px",
                  }}
                >
                  {tag}
                </Typography>
              )}
            </Stack>
            {typeof body === "string" ? (
              <Typography variant="body1" height={"100%"}>
                {body}
              </Typography>
            ) : (
              body
            )}
            {links && (
              <Stack direction="row" spacing={2}>
                {links.map(({ label, href, type = "link" }) => (
                  <MdaiLink
                    key={label}
                    label={label}
                    href={href}
                    color="primary"
                    variant="outlined"
                    externalIcons={false}
                    type={type}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
